import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";

const Shell = ({ children }) => (
  <div className="min-h-screen blueprint-grid">
    <AppHeader />
    <main className="max-w-lg mx-auto px-6 py-24 text-center rise">{children}</main>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const sid = params.get("session_id"), project = params.get("project");

  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < 20 && alive; i++) {
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            setState("paid");
            setTimeout(() => navigate(project ? `/project/${project}` : "/dashboard", { replace: true }), 1500);
            return;
          }
          if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
        } catch { /* retry */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (alive) setState("timeout");
    })();
    return () => { alive = false; };
  }, [sid, project, navigate]);

  return (
    <Shell>
      <div data-testid="payment-status" data-state={state}>
        {state === "checking" && <><Loader2 size={40} className="mx-auto animate-spin text-[var(--cyan)]" /><h1 className="mt-6 text-2xl font-bold">Confirming your payment…</h1></>}
        {state === "paid" && <><CheckCircle2 size={44} className="mx-auto text-[var(--emerald)]" /><h1 className="mt-6 text-2xl font-bold">Payment confirmed</h1><p className="mt-2 text-[var(--muted)]">{project ? "Opening your draft — the AI starts now." : "Welcome to RhinoDraft AI."}</p></>}
        {state !== "checking" && state !== "paid" && (
          <><XCircle size={44} className="mx-auto text-red-400" /><h1 className="mt-6 text-2xl font-bold">{state === "failed" ? "Payment didn't go through" : "Still waiting for confirmation"}</h1>
            <p className="mt-2 text-[var(--muted)]">{state === "failed" ? "No charge was made." : "Refresh in a moment — if you were charged, access unlocks automatically."}</p>
            <Link to="/dashboard" className="mt-6 inline-block text-[var(--cyan)] hover:underline" data-testid="payment-back-link">Back to dashboard</Link></>
        )}
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const project = params.get("project");
  return (
    <Shell>
      <XCircle size={44} className="mx-auto text-[var(--muted)]" />
      <h1 className="mt-6 text-2xl font-bold" data-testid="payment-cancelled">Checkout cancelled</h1>
      <p className="mt-2 text-[var(--muted)]">No charge was made.</p>
      <Link to={project ? `/project/${project}` : "/dashboard"} className="mt-6 inline-block text-[var(--cyan)] hover:underline" data-testid="payment-back-link">Go back</Link>
    </Shell>
  );
}
